<template>
  <div class="studio-docs" :class="{ 'studio-docs--embedded': isLoggedIn }">
    <header v-if="!isLoggedIn" class="docs-public-nav">
      <div class="docs-public-nav__inner">
        <router-link to="/" class="docs-public-nav__brand">
          <img src="/logo-banner.png" alt="SocialLabs" class="docs-public-nav__logo" />
        </router-link>
        <div class="docs-public-nav__actions">
          <LocaleSwitcher />
          <router-link v-if="isLoggedIn" to="/dashboard" class="btn btn-sm btn-outline-primary">
            {{ $t('dashboard.title') }}
          </router-link>
          <router-link v-else :to="loginRedirect" class="btn btn-sm btn-outline-primary">
            {{ $t('auth.sign_in') }}
          </router-link>
          <router-link to="/" class="btn btn-sm btn-link text-muted">{{ $t('legal.back_home') }}</router-link>
        </div>
      </div>
    </header>

  <PageLayout
    :title="$t('studioApi.docs_title')"
    :subtitle="$t('studioApi.docs_subtitle')"
  >
    <template #actions>
      <button type="button" class="btn btn-outline-secondary d-lg-none" @click="navOpen = !navOpen">
        <i class="fas fa-bars"></i>
      </button>
    </template>

    <div class="docs-page mb-30">
      <div class="docs userDatatable global-shadow border bg-white radius-xl w-100">
        <div class="docs-shell">
        <aside class="docs-nav" :class="{ open: navOpen }">
        <div v-for="group in navGroups" :key="group.id" class="docs-group">
          <button type="button" class="docs-group__head" @click="toggleGroup(group.id)">
            <span>{{ group.label }}</span>
            <i class="fas" :class="openGroups[group.id] ? 'fas fa-caret-down' : 'fas fa-chevron-right'"></i>
          </button>
          <div v-show="openGroups[group.id]" class="docs-group__body">
            <button
              v-for="item in group.items"
              :key="item.id"
              type="button"
              class="docs-nav-item"
              :class="{ active: activeId === item.id }"
              @click="select(item.id)"
            >
              <span class="docs-nav-item__title">{{ item.title }}</span>
              <span v-if="item.method" class="docs-verb" :class="item.method.toLowerCase()">{{ item.method }}</span>
            </button>
          </div>
        </div>
      </aside>
      <div v-if="navOpen" class="docs-scrim" @click="navOpen = false"></div>

      <main ref="main" class="docs-main">
        <p class="docs-kicker">Studio Public API</p>
        <h1>{{ page.section }}</h1>
        <div class="docs-base">
          <code>{{ apiBase }}</code>
          <button type="button" class="docs-icon-btn" :title="$t('studioApi.copy_base_url')" @click="copyText(apiBase)">
            <i class="fas fa-copy"></i>
          </button>
        </div>
        <p class="docs-lead">{{ page.lead }}</p>

        <template v-if="page.id === 'api-key'">
          <div v-if="!isLoggedIn" class="docs-card docs-card--cta">
            <h3>{{ $t('studioApi.your_api_key') }}</h3>
            <p class="mb-3">{{ $t('studioApi.public_login_hint') }}</p>
            <div class="d-flex flex-wrap gap-2">
              <router-link :to="loginRedirect" class="docs-btn">{{ $t('auth.sign_in') }}</router-link>
              <router-link to="/register" class="docs-btn docs-btn--ghost">{{ $t('auth.register') }}</router-link>
            </div>
          </div>
          <div v-else class="docs-card">
            <div class="docs-card__row">
              <div>
                <h3>{{ $t('studioApi.your_api_key') }}</h3>
                <p>{{ $t('studioApi.api_key_desc') }}</p>
              </div>
              <button type="button" class="docs-btn" :disabled="resettingKey" @click="onResetKey">
                {{ resettingKey ? $t('studioApi.generating') : (apiKey ? $t('studioApi.regenerate') : $t('studioApi.generate_key')) }}
              </button>
            </div>
            <div v-if="apiKey" class="docs-key">
              <code>{{ keyDisplay }}</code>
              <button type="button" class="docs-btn docs-btn--ghost" @click="showKey = !showKey">{{ showKey ? $t('common.hide') : $t('common.show') }}</button>
              <button type="button" class="docs-btn docs-btn--ghost" @click="copyText(apiKey)">{{ $t('common.copy') }}</button>
            </div>
            <p v-else class="docs-empty">{{ $t('studioApi.generate_key_prompt') }}</p>
            <div class="docs-postman mt-3">
              <button type="button" class="docs-btn docs-btn--ghost" @click="downloadPostmanCollection">
                <i class="fas fa-download mr-1"></i> {{ $t('studioApi.open_in_postman') }}
              </button>
              <a class="docs-btn docs-btn--ghost" :href="postmanStaticUrl" download="sociallabs-studio-api.postman_collection.json">
                {{ $t('studioApi.postman_direct_link') }}
              </a>
            </div>
          </div>
        </template>

        <template v-else-if="page.id === 'auth'">
          <h2>Send the key with every request</h2>
          <p class="docs-copy">Header is preferred. Query string is only for <code>&lt;img&gt;</code> or direct download URLs.</p>
          <div class="docs-schema">
            <div v-for="row in authMethods" :key="row.name" class="docs-schema__row">
              <div class="docs-schema__name">
                <code>{{ row.name }}</code>
                <span v-if="row.badge" class="docs-pill">{{ row.badge }}</span>
              </div>
              <p class="docs-schema__desc"><code>{{ row.example }}</code></p>
            </div>
          </div>
        </template>

        <template v-else-if="page.id === 'designs'">
          <h2>Template IDs</h2>
          <p class="docs-copy">Use these IDs in <code>/public/studio/:id</code> requests.</p>
          <div v-if="loadingTemplates" class="docs-empty">Loading designs…</div>
          <div v-else-if="!templates.length" class="docs-empty">
            <p v-if="isLoggedIn">{{ $t('studioApi.no_designs_yet') }} <router-link to="/ai/studio">{{ $t('studioApi.create_one') }}</router-link></p>
            <p v-else>{{ $t('studioApi.public_designs_hint') }}</p>
          </div>
          <div v-else class="docs-table-wrap">
            <table class="docs-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Size</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="t in templates" :key="t.id">
                  <td>
                    <code>{{ t.id }}</code>
                    <button type="button" class="docs-icon-btn" title="Copy ID" @click="copyText(t.id)">
                      <i class="fas fa-copy"></i>
                    </button>
                  </td>
                  <td>{{ t.name }}</td>
                  <td class="muted">{{ t.width }}×{{ t.height }}</td>
                  <td class="muted">{{ formatDate(t.updated_at || t.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <template v-else-if="page.id === 'overview'">
          <h2>Quick start</h2>
          <p class="docs-copy mb-3">
            {{ $t('studioApi.public_docs_note') }}
            <a href="/llms.txt" target="_blank" rel="noopener">llms.txt</a>
          </p>
          <ol class="docs-steps">
            <li>Generate an API key.</li>
            <li>List designs with <code>GET /public/studio</code> or copy an ID below.</li>
            <li>Fetch field keys with <code>GET /public/studio/:id</code> (<code>page</code> starts at 1).</li>
            <li>Render PNG or JPEG with <code>GET</code> or <code>POST /public/studio/:id/download</code>.</li>
            <li>Import from Canva — see <a href="#canva-import" @click.prevent="select('canva-import')">Canva import guide</a> (Bearer JWT, then render with API key).</li>
            <li>Automate with n8n — see <a href="#n8n" @click.prevent="select('n8n')">n8n automation</a> (community node <code>n8n-nodes-sociallabs</code>).</li>
          </ol>
          <p class="docs-copy">
            In <code>fields</code>, include only keys you want to change. Omitted keys, empty strings, and whitespace-only values keep the layer’s current text or image URL from the saved design.
          </p>
          <p class="docs-copy">
            The download raster matches Design Studio: fonts, type size, crop, and overflow.
          </p>
          <div class="docs-kv">
            <span>Auth</span>
            <code>X-API-Key: {{ sampleKey }}</code>
            <button v-if="apiKey" type="button" class="docs-btn docs-btn--ghost" @click="showKey = !showKey">{{ showKey ? $t('common.hide') : $t('common.show') }}</button>
          </div>
        </template>

        <template v-else-if="page.id === 'canva-import'">
          <h2>{{ $t('studioApi.canva_guide_title') }}</h2>
          <p class="docs-copy">{{ $t('studioApi.canva_guide_intro') }}</p>
          <h3>{{ $t('studioApi.canva_guide_prereq_title') }}</h3>
          <ul class="docs-copy docs-list">
            <li>{{ $t('studioApi.canva_guide_prereq_plan') }}</li>
            <li>{{ $t('studioApi.canva_guide_prereq_quota') }}</li>
            <li>{{ $t('studioApi.canva_guide_prereq_url') }}</li>
          </ul>
          <h3>{{ $t('studioApi.canva_guide_auth_title') }}</h3>
          <p class="docs-copy">{{ $t('studioApi.canva_guide_auth_lead') }}</p>
          <div class="docs-endpoint">
            <span class="docs-verb post">POST</span>
            <code>{{ docUrl('/auth/login') }}</code>
          </div>
          <p class="docs-copy">{{ $t('studioApi.canva_guide_login_hint') }}</p>
          <h3>{{ $t('studioApi.canva_guide_flow_title') }}</h3>
          <ol class="docs-steps">
            <li>
              <a href="#canvaStart" @click.prevent="select('canvaStart')">{{ $t('studioApi.nav_canva_start') }}</a>
              — {{ $t('studioApi.canva_step_start') }}
            </li>
            <li>
              <a href="#canvaImportJob" @click.prevent="select('canvaImportJob')">{{ $t('studioApi.nav_canva_job') }}</a>
              — {{ $t('studioApi.canva_step_poll') }}
            </li>
            <li>{{ $t('studioApi.canva_step_done') }}</li>
            <li>
              <a href="#downloadPost" @click.prevent="select('downloadPost')">{{ $t('studioApi.nav_render_json') }}</a>
              — {{ $t('studioApi.canva_step_render') }}
            </li>
          </ol>
          <h3>{{ $t('studioApi.canva_job_status_title') }}</h3>
          <div class="docs-table-wrap">
            <table class="docs-table">
              <thead>
                <tr>
                  <th>{{ $t('studioApi.canva_col_status') }}</th>
                  <th>{{ $t('studioApi.canva_col_meaning') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in canvaStatusRows" :key="row.status">
                  <td><code>{{ row.status }}</code></td>
                  <td class="muted">{{ row.desc }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p class="docs-copy">{{ $t('studioApi.canva_job_ttl_hint') }}</p>
          <h3>{{ $t('studioApi.canva_n8n_title') }}</h3>
          <p class="docs-copy">{{ $t('studioApi.canva_n8n_lead') }}</p>
          <section v-for="flow in canvaN8nFlows" :key="flow.id" class="docs-n8n-flow">
            <ol class="docs-n8n-nodes">
              <li v-for="(node, idx) in flow.nodes" :key="flow.id + '-' + idx">
                <div class="docs-n8n-node__head">
                  <span class="docs-n8n-node__type">{{ node.type }}</span>
                  <strong>{{ node.name }}</strong>
                </div>
                <p v-if="node.desc" class="docs-copy docs-n8n-node__desc">{{ node.desc }}</p>
                <pre v-if="node.config" class="docs-code docs-code--compact">{{ node.config }}</pre>
              </li>
            </ol>
          </section>
        </template>

        <template v-else-if="page.id === 'mcp'">
          <h2>Connect an LLM</h2>
          <p class="docs-copy">
            Use the hosted MCP endpoint to let Claude, ChatGPT, Cursor, or any MCP client list designs, read fields, and render images that match Design Studio.
          </p>
          <div class="docs-kv">
            <span>URL</span>
            <code>{{ mcpUrl }}</code>
            <button type="button" class="docs-icon-btn" title="Copy URL" @click="copyText(mcpUrl)">
              <i class="fas fa-copy"></i>
            </button>
          </div>
          <div class="docs-kv">
            <span>Auth</span>
            <code>X-API-Key: {{ sampleKey }}</code>
            <button v-if="apiKey" type="button" class="docs-btn docs-btn--ghost" @click="showKey = !showKey">{{ showKey ? $t('common.hide') : $t('common.show') }}</button>
          </div>
          <h3>Tools exposed to the LLM</h3>
          <div class="docs-schema">
            <div v-for="row in mcpTools" :key="row.name" class="docs-schema__row">
              <div class="docs-schema__name">
                <code>{{ row.name }}</code>
              </div>
              <p class="docs-schema__desc">{{ row.desc }}</p>
            </div>
          </div>
          <h3>Claude Desktop</h3>
          <p class="docs-copy">Add this to your Claude Desktop MCP config. Replace the API key with yours from the API Key page.</p>
        </template>

        <template v-else-if="page.id === 'n8n'">
          <h2>{{ $t('studioApi.nav_n8n') }}</h2>
          <p class="docs-copy">{{ $t('studioApi.n8n_intro') }}</p>
          <div v-if="n8nEditorUrl" class="docs-kv">
            <span>{{ $t('studioApi.n8n_editor_url') }}</span>
            <a :href="n8nEditorUrl" target="_blank" rel="noopener">{{ n8nEditorUrl }}</a>
          </div>
          <p v-else class="docs-copy">{{ $t('studioApi.n8n_editor_local') }}</p>
          <h3>{{ $t('studioApi.n8n_install_title') }}</h3>
          <p class="docs-copy">{{ $t('studioApi.n8n_install_lead') }}</p>
          <ol class="docs-steps">
            <li>{{ $t('studioApi.n8n_install_step1') }}</li>
            <li>{{ $t('studioApi.n8n_install_step2') }}</li>
            <li>{{ $t('studioApi.n8n_install_step3') }}</li>
          </ol>
          <p class="docs-copy">{{ $t('studioApi.n8n_credential_hint') }}</p>
          <p class="docs-copy">{{ $t('studioApi.n8n_nodes_hint') }}</p>

          <section v-for="flow in n8nFlows" :key="flow.id" class="docs-n8n-flow">
            <h3>{{ flow.title }}</h3>
            <p class="docs-copy">{{ flow.lead }}</p>
            <ol class="docs-n8n-nodes">
              <li v-for="(node, idx) in flow.nodes" :key="flow.id + '-' + idx">
                <div class="docs-n8n-node__head">
                  <span class="docs-n8n-node__type">{{ node.type }}</span>
                  <strong>{{ node.name }}</strong>
                </div>
                <p v-if="node.desc" class="docs-copy docs-n8n-node__desc">{{ node.desc }}</p>
                <pre v-if="node.config" class="docs-code docs-code--compact">{{ node.config }}</pre>
              </li>
            </ol>
          </section>

          <p class="docs-copy mt-3">{{ $t('studioApi.n8n_image_fields_hint') }}</p>
          <p class="docs-copy">
            {{ $t('studioApi.n8n_canva_note') }}
            <a href="#canva-import" @click.prevent="select('canva-import')">{{ $t('studioApi.nav_canva_guide') }}</a>.
          </p>
        </template>

        <template v-else-if="page.id === 'limits'">
          <h2>Per API key</h2>
          <p class="docs-copy">
            Limits are counted per API key in UTC windows. Render is CPU-heavy, so it is the tightest bucket.
            Exceeding a limit returns <code>429</code> with <code>Retry-After</code>.
          </p>
          <div class="docs-schema">
            <div v-for="row in rateLimitRows" :key="row.name" class="docs-schema__row">
              <div class="docs-schema__name">
                <code>{{ row.name }}</code>
                <span class="docs-type">{{ row.limit }}</span>
              </div>
              <p class="docs-schema__desc">{{ row.desc }}</p>
            </div>
          </div>
          <h3>Response headers</h3>
          <div class="docs-schema">
            <div v-for="row in rateLimitHeaders" :key="row.name" class="docs-schema__row">
              <div class="docs-schema__name">
                <code>{{ row.name }}</code>
              </div>
              <p class="docs-schema__desc">{{ row.desc }}</p>
            </div>
          </div>
        </template>

        <template v-else-if="page.id === 'errors'">
          <h2>Error responses</h2>
          <p class="docs-copy">Failures still return JSON with <code>success: false</code>.</p>
          <div class="docs-schema">
            <div v-for="row in errorRows" :key="row.code" class="docs-schema__row">
              <div class="docs-schema__name">
                <code>{{ row.code }}</code>
                <span class="docs-type">{{ row.label }}</span>
              </div>
              <p class="docs-schema__desc">{{ row.desc }}</p>
            </div>
          </div>
        </template>

        <template v-else>
          <h2>{{ page.title }}</h2>
          <div class="docs-endpoint">
            <span class="docs-verb" :class="page.method.toLowerCase()">{{ page.method }}</span>
            <code>{{ docUrl(page.path) }}</code>
            <button type="button" class="docs-icon-btn" title="Copy URL" @click="copyText(docUrl(page.path))">
              <i class="fas fa-copy"></i>
            </button>
          </div>
          <p class="docs-copy">{{ page.description }}</p>
          <p v-if="!isCanvaEndpoint" class="docs-copy">
            Field keys follow <code>page_1_text_1</code>, <code>page_1_image_1</code>, <code>page_2_text_1</code>, …
            (page number starts at 1, then text/image index on that page). You can also pass the layer <code>id</code>.
            Image values must be a public HTTP(S) URL. Output matches the Design Studio canvas.
          </p>
          <p v-else class="docs-copy">
            {{ $t('studioApi.canva_endpoint_auth') }}
            <a href="#canva-import" @click.prevent="select('canva-import')">{{ $t('studioApi.nav_canva_guide') }}</a>.
          </p>

          <h3 v-if="page.params && page.params.length">Parameters</h3>
          <div v-if="page.params && page.params.length" class="docs-schema">
            <div v-for="param in page.params" :key="page.id + '-' + param.name" class="docs-schema__row">
              <div class="docs-schema__name">
                <code>{{ param.name }}</code>
                <span class="docs-type">{{ param.type }}</span>
                <span class="docs-in">{{ param.in }}</span>
                <span v-if="param.required" class="docs-req">required</span>
              </div>
              <p class="docs-schema__desc">{{ param.description }}</p>
            </div>
          </div>

          <h3>Responses</h3>
          <div class="docs-resp">
            <div class="docs-resp__tabs">
              <button
                v-for="code in page.responses"
                :key="code.status"
                type="button"
                class="docs-resp__tab"
                :class="{ active: activeResponse === code.status }"
                @click="activeResponse = code.status"
              >{{ code.label }}</button>
            </div>
            <div class="docs-schema docs-schema--flush">
              <div v-for="field in activeSchema" :key="field.path" class="docs-schema__row" :style="{ paddingLeft: (14 + field.depth * 16) + 'px' }">
                <div class="docs-schema__name">
                  <code>{{ field.name }}</code>
                  <span class="docs-type">{{ field.type }}</span>
                </div>
                <p v-if="field.desc" class="docs-schema__desc">{{ field.desc }}</p>
              </div>
            </div>
          </div>
        </template>

        <div class="docs-pager">
          <button v-if="prevItem" type="button" class="docs-pager__btn" @click="select(prevItem.id)">
            ← {{ prevItem.title }}
          </button>
          <span v-else></span>
          <button v-if="nextItem" type="button" class="docs-pager__btn docs-pager__btn--next" @click="select(nextItem.id)">
            {{ nextItem.title }} →
          </button>
        </div>
      </main>
        </div>
      </div>

      <aside class="docs-side userDatatable global-shadow border bg-white radius-xl">
        <section class="docs-panel">
          <header class="docs-panel__head">
            <span>
              <span v-if="page.method" class="docs-verb" :class="page.method.toLowerCase()">{{ page.method }}</span>
              {{ page.sidePath }}
            </span>
            <div class="docs-panel__tools">
              <select v-model="activeLang" class="docs-select">
                <option v-for="tab in exampleTabs" :key="tab.id" :value="tab.id">{{ tab.label }}</option>
              </select>
              <button
                v-if="page.kind === 'endpoint'"
                type="button"
                class="docs-icon-btn"
                :disabled="trying"
                :title="trying ? $t('studioApi.running') : $t('studioApi.send_request')"
                @click="tryRequest"
              >
                <i :class="trying ? 'fas fa-spinner fa-spin' : 'fas fa-play'"></i>
              </button>
              <button type="button" class="docs-icon-btn" :title="$t('common.copy')" @click="copyText(requestSample)">
                <i class="fas fa-copy"></i>
              </button>
            </div>
          </header>
          <pre class="docs-code" v-html="highlightedRequest"></pre>
        </section>

        <section class="docs-panel">
          <header class="docs-panel__head">
            <span>{{ $t('studioApi.example_responses') }} <i class="fas fa-info-circle docs-info"></i></span>
            <select v-model="activeResponse" class="docs-select">
              <option v-for="code in page.responses" :key="code.status" :value="code.status">{{ code.status }}</option>
            </select>
          </header>
          <div v-if="tryImage" class="docs-preview">
            <img :src="tryImage" alt="Rendered design" />
          </div>
          <pre v-else class="docs-code" v-html="highlightedResponse"></pre>
          <footer class="docs-panel__foot">{{ activeContentType }}</footer>
        </section>
      </aside>
    </div>
  </PageLayout>
  </div>
</template>

<script>
import LocaleSwitcher from '@/components/LocaleSwitcher.vue'
import { mapActions, mapGetters } from 'vuex'
import { buildStudioPostmanCollection, downloadStudioPostmanCollection } from '@/utils/studioPostman'

const JSON_OK = [
  { name: 'success', type: 'boolean', desc: 'true when the request succeeds' },
  {
    name: 'data',
    type: 'object',
    desc: 'Payload',
    children: [
      {
        name: 'design',
        type: 'object',
        children: [
          { name: 'id', type: 'string', desc: 'Design UUID' },
          { name: 'name', type: 'string' },
          { name: 'width', type: 'integer' },
          { name: 'height', type: 'integer' },
          { name: 'pages', type: 'integer' },
          { name: 'fields', type: 'array', desc: 'Editable text and image layers. Keys are page_N_text_M / page_N_image_M (page starts at 1). Each entry includes the current default value.' },
        ],
      },
    ],
  },
  { name: 'message', type: 'string' },
]

const JSON_LIST = [
  { name: 'success', type: 'boolean', desc: 'true when the request succeeds' },
  {
    name: 'data',
    type: 'object',
    desc: 'Payload',
    children: [
      {
        name: 'designs',
        type: 'array',
        desc: 'Designs owned by this API key',
        children: [
          { name: 'id', type: 'string', desc: 'Design UUID' },
          { name: 'name', type: 'string' },
          { name: 'width', type: 'integer' },
          { name: 'height', type: 'integer' },
          { name: 'pages', type: 'integer', desc: 'Page count. Page numbers start at 1.' },
        ],
      },
    ],
  },
  { name: 'message', type: 'string' },
]

const JSON_ERR = [
  { name: 'success', type: 'boolean', desc: 'false' },
  { name: 'data', type: 'object', desc: 'Usually empty' },
  { name: 'message', type: 'string', desc: 'Error description' },
]

const JSON_CANVA_JOB_FIELDS = [
  { name: 'id', type: 'string', desc: 'Job UUID' },
  { name: 'status', type: 'string', desc: 'queued | fetching | downloading | saving | done | error' },
  { name: 'message', type: 'string', desc: 'Human-readable progress' },
  { name: 'percent', type: 'integer', desc: '0–100' },
  { name: 'done', type: 'integer', desc: 'Assets downloaded (when applicable)' },
  { name: 'total', type: 'integer', desc: 'Total assets (when applicable)' },
  { name: 'template_id', type: 'string', desc: 'Design UUID when status is done' },
  { name: 'template_name', type: 'string', desc: 'Name in Design Hub when done' },
  { name: 'error', type: 'string', desc: 'Set when status is error' },
  { name: 'url', type: 'string', desc: 'Normalized Canva URL' },
]

const JSON_CANVA_JOB = [
  { name: 'success', type: 'boolean', desc: 'true when the request succeeds' },
  {
    name: 'data',
    type: 'object',
    children: [
      { name: 'job', type: 'object', desc: 'Import job', children: JSON_CANVA_JOB_FIELDS },
    ],
  },
  { name: 'message', type: 'string' },
]

const JSON_CANVA_JOBS = [
  { name: 'success', type: 'boolean', desc: 'true when the request succeeds' },
  {
    name: 'data',
    type: 'object',
    children: [
      {
        name: 'jobs',
        type: 'array',
        desc: 'Recent jobs for this user',
        children: JSON_CANVA_JOB_FIELDS,
      },
    ],
  },
  { name: 'message', type: 'string' },
]

const IMAGE_OK = [
  { name: 'body', type: 'binary', desc: 'Rendered PNG or JPEG bytes' },
]

function flattenSchema(nodes, depth, prefix) {
  const out = []
  ;(nodes || []).forEach((node) => {
    const path = prefix ? `${prefix}.${node.name}` : node.name
    out.push({
      name: node.name,
      type: node.type,
      desc: node.desc || '',
      depth: depth || 0,
      path,
    })
    if (node.children && node.children.length) {
      out.push(...flattenSchema(node.children, (depth || 0) + 1, path))
    }
  })
  return out
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

export default {
  name: 'StudioApiDocs',
  components: { LocaleSwitcher },
  data() {
    return {
      activeId: 'overview',
      activeLang: 'curl',
      activeResponse: '200',
      navOpen: false,
      showKey: false,
      resettingKey: false,
      trying: false,
      tryImage: '',
      tryOverride: '',
      openGroups: { start: true, canva: true, studio: true, errors: true },
      exampleTabs: [
        { id: 'curl', label: 'cURL' },
        { id: 'js', label: 'JavaScript' },
        { id: 'python', label: 'Python' },
      ],
      navGroupDefs: [
        {
          id: 'start',
          labelKey: 'studioApi.nav_getting_started',
          items: [
            { id: 'overview', titleKey: 'studioApi.nav_overview' },
            { id: 'api-key', titleKey: 'studioApi.nav_api_key' },
            { id: 'auth', titleKey: 'studioApi.nav_auth' },
            { id: 'designs', titleKey: 'studioApi.nav_designs' },
            { id: 'canva-import', titleKey: 'studioApi.nav_canva_guide' },
            { id: 'mcp', titleKey: 'studioApi.nav_mcp' },
            { id: 'n8n', titleKey: 'studioApi.nav_n8n' },
            { id: 'limits', titleKey: 'studioApi.nav_rate_limits' },
          ],
        },
        {
          id: 'canva',
          labelKey: 'studioApi.nav_canva',
          items: [
            { id: 'canvaStart', titleKey: 'studioApi.nav_canva_start', method: 'POST' },
            { id: 'canvaImportList', titleKey: 'studioApi.nav_canva_jobs', method: 'GET' },
            { id: 'canvaImportJob', titleKey: 'studioApi.nav_canva_job', method: 'GET' },
          ],
        },
        {
          id: 'studio',
          labelKey: 'studioApi.nav_studio',
          items: [
            { id: 'list', titleKey: 'studioApi.nav_list_designs', method: 'GET' },
            { id: 'meta', titleKey: 'studioApi.nav_get_metadata', method: 'GET' },
            { id: 'downloadPost', titleKey: 'studioApi.nav_render_json', method: 'POST' },
          ],
        },
        {
          id: 'errors',
          labelKey: 'studioApi.nav_errors',
          items: [{ id: 'errors', titleKey: 'studioApi.nav_status_codes' }],
        },
      ],
      pages: {
        overview: {
          id: 'overview',
          kind: 'guide',
          section: 'Studio',
          title: 'Overview',
          lead: 'Render any saved design as PNG or JPEG, pixel-matched to the Design Studio canvas.',
          sidePath: 'GET /public/studio/:id',
          method: 'GET',
          responses: [
            { status: '200', label: '200 OK', schema: JSON_OK, contentType: 'application/json' },
          ],
        },
        'api-key': {
          id: 'api-key',
          kind: 'guide',
          section: 'API Key',
          title: 'API Key',
          lead: 'Authenticate with your account API key.',
          sidePath: 'X-API-Key',
          responses: [
            { status: '200', label: '200 OK', schema: JSON_OK, contentType: 'application/json' },
          ],
        },
        auth: {
          id: 'auth',
          kind: 'guide',
          section: 'Authentication',
          title: 'Authentication',
          lead: 'Every public studio request must include this account’s API key.',
          sidePath: 'X-API-Key',
          responses: [
            { status: '401', label: '401 Unauthorized', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
        designs: {
          id: 'designs',
          kind: 'guide',
          section: 'Designs',
          title: 'Designs',
          lead: 'Each saved design has a UUID. List them with GET /public/studio or copy an ID here.',
          sidePath: 'GET /public/studio',
          method: 'GET',
          responses: [
            { status: '200', label: '200 OK', schema: JSON_OK, contentType: 'application/json' },
          ],
        },
        'canva-import': {
          id: 'canva-import',
          kind: 'guide',
          section: 'Canva import',
          title: 'Import from Canva',
          lead: 'Turn a public Canva share link into a Studio design, then render with your API key.',
          sidePath: 'POST /ai/studio/import-canva',
          method: 'POST',
          responses: [
            { status: '202', label: '202 Accepted', schema: JSON_CANVA_JOB, contentType: 'application/json' },
            { status: '401', label: '401', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
        canvaStart: {
          id: 'canvaStart',
          kind: 'endpoint',
          section: 'Canva import',
          title: 'Start Canva import',
          lead: 'Queue an async import from a Canva share or design URL.',
          description: 'Requires a logged-in user Bearer token (not the Studio API key). Returns a job id for polling. Counts against your plan Canva import quota.',
          method: 'POST',
          path: '/ai/studio/import-canva',
          sidePath: 'POST /ai/studio/import-canva',
          params: [
            { name: 'url', in: 'body', type: 'string', required: true, description: 'Public Canva link: canva.link/… or canva.com/design/…' },
          ],
          responses: [
            { status: '202', label: '202 Accepted', schema: JSON_CANVA_JOB, contentType: 'application/json' },
            { status: '400', label: '400', schema: JSON_ERR, contentType: 'application/json' },
            { status: '401', label: '401', schema: JSON_ERR, contentType: 'application/json' },
            { status: '402', label: '402', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
        canvaImportList: {
          id: 'canvaImportList',
          kind: 'endpoint',
          section: 'Canva import',
          title: 'List import jobs',
          lead: 'Recent Canva import jobs for the authenticated user.',
          description: 'Returns up to the latest jobs stored for your account (about 24 hours). Use GET /ai/studio/import-canva/jobs/:id for one job.',
          method: 'GET',
          path: '/ai/studio/import-canva/jobs',
          sidePath: 'GET /ai/studio/import-canva/jobs',
          params: [],
          responses: [
            { status: '200', label: '200 OK', schema: JSON_CANVA_JOBS, contentType: 'application/json' },
            { status: '401', label: '401', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
        canvaImportJob: {
          id: 'canvaImportJob',
          kind: 'endpoint',
          section: 'Canva import',
          title: 'Get import job',
          lead: 'Poll until status is done or error.',
          description: 'When status is done, template_id is the design UUID for GET /public/studio/:id and render. Poll about every 3–5 seconds; heavy imports can take several minutes.',
          method: 'GET',
          path: '/ai/studio/import-canva/jobs/:id',
          sidePath: 'GET /ai/studio/import-canva/jobs/:id',
          params: [
            { name: 'id', in: 'path', type: 'uuid', required: true, description: 'Job id from the start import response' },
          ],
          responses: [
            { status: '200', label: '200 OK', schema: JSON_CANVA_JOB, contentType: 'application/json' },
            { status: '401', label: '401', schema: JSON_ERR, contentType: 'application/json' },
            { status: '404', label: '404', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
        mcp: {
          id: 'mcp',
          kind: 'guide',
          section: 'MCP',
          title: 'MCP for LLMs',
          lead: 'Connect any MCP-compatible AI client. Rendered images match Design Studio.',
          sidePath: 'POST /mcp',
          method: 'POST',
          responses: [
            { status: '200', label: '200 OK', schema: JSON_OK, contentType: 'application/json' },
          ],
        },
        limits: {
          id: 'limits',
          kind: 'guide',
          section: 'Rate limits',
          title: 'Rate limits',
          lead: 'Public studio endpoints are rate-limited per API key. Back off on 429.',
          sidePath: '429 Too Many Requests',
          responses: [
            { status: '429', label: '429', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
        list: {
          id: 'list',
          kind: 'endpoint',
          section: 'Studio',
          title: 'List designs',
          lead: 'List every design this API key can render.',
          description: 'Returns id, name, size, and page count. Call this first when you do not already have a UUID. Use GET /public/studio/:id next to read editable field keys.',
          method: 'GET',
          path: '/public/studio',
          sidePath: 'GET /public/studio',
          params: [],
          responses: [
            { status: '200', label: '200 OK', schema: JSON_LIST, contentType: 'application/json' },
            { status: '401', label: '401', schema: JSON_ERR, contentType: 'application/json' },
            { status: '429', label: '429', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
        meta: {
          id: 'meta',
          kind: 'endpoint',
          section: 'Studio',
          title: 'Get design metadata',
          lead: 'Discover size, pages, and editable fields.',
          description: 'Call this first to learn which field keys you can override when rendering. Pages start at 1. Pass page to list fields for one page only (e.g. page_1_text_1).',
          method: 'GET',
          path: '/public/studio/:id',
          sidePath: 'GET /public/studio/:id',
          params: [
            { name: 'id', in: 'path', type: 'uuid', required: true, description: 'Design UUID from the Designs list' },
            { name: 'page', in: 'query', type: 'integer', required: false, description: 'Page number (starts at 1). When set, only fields on that page are returned.' },
          ],
          responses: [
            { status: '200', label: '200 OK', schema: JSON_OK, contentType: 'application/json' },
            { status: '400', label: '400', schema: JSON_ERR, contentType: 'application/json' },
            { status: '401', label: '401', schema: JSON_ERR, contentType: 'application/json' },
            { status: '404', label: '404', schema: JSON_ERR, contentType: 'application/json' },
            { status: '429', label: '429', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
        downloadPost: {
          id: 'downloadPost',
          kind: 'endpoint',
          section: 'Studio',
          title: 'Render design',
          lead: 'Download PNG or JPEG with many or long field values.',
          description: 'Send page, format, and optional field overrides as JSON. Only include keys you want to change; empty values are ignored and the saved design default is used. The response body is the rendered image, not JSON. Pages start at 1.',
          method: 'POST',
          path: '/public/studio/:id/download',
          sidePath: 'POST /public/studio/:id/download',
          params: [
            { name: 'id', in: 'path', type: 'uuid', required: true, description: 'Design UUID from the Designs list' },
            { name: 'page', in: 'body', type: 'integer', required: false, description: 'Page number (starts at 1). Default: 1' },
            { name: 'format', in: 'body', type: 'string', required: false, description: 'png, jpeg, or jpg. Default: png' },
            { name: 'fields', in: 'body', type: 'object', required: false, description: 'Partial map of field key → value. Omitted or empty keys keep design defaults, e.g. { "page_1_text_1": "50% OFF" }' },
          ],
          responses: [
            { status: '200', label: '200 OK', schema: IMAGE_OK, contentType: 'image/png' },
            { status: '400', label: '400', schema: JSON_ERR, contentType: 'application/json' },
            { status: '401', label: '401', schema: JSON_ERR, contentType: 'application/json' },
            { status: '404', label: '404', schema: JSON_ERR, contentType: 'application/json' },
            { status: '429', label: '429', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
        n8n: {
          id: 'n8n',
          kind: 'guide',
          section: 'Automation',
          title: 'n8n automation',
          lead: 'Install the n8n-nodes-sociallabs community package and use the SocialLabs Studio node (Get Designs, Get Metadata, Render).',
          sidePath: 'SocialLabs Studio node',
          responses: [
            { status: '200', label: '200 OK', schema: JSON_OK, contentType: 'application/json' },
          ],
        },
        errors: {
          id: 'errors',
          kind: 'guide',
          section: 'Errors',
          title: 'Status codes',
          lead: 'Common failure cases for the public studio API.',
          sidePath: 'application/json',
          responses: [
            { status: '401', label: '401', schema: JSON_ERR, contentType: 'application/json' },
            { status: '404', label: '404', schema: JSON_ERR, contentType: 'application/json' },
            { status: '400', label: '400', schema: JSON_ERR, contentType: 'application/json' },
            { status: '429', label: '429', schema: JSON_ERR, contentType: 'application/json' },
          ],
        },
      },
    }
  },
  computed: {
    ...mapGetters('ai', ['templates', 'loading']),
    ...mapGetters('auth', ['user', 'isLoggedIn']),
    loginRedirect() {
      return { path: '/login', query: { redirect: this.$route.fullPath } }
    },
    postmanStaticUrl() {
      return this.$t('studioApi.postman_static_url')
    },
    loadingTemplates() {
      return this.loading && !this.templates.length
    },
    navGroups() {
      return this.navGroupDefs.map((group) => ({
        id: group.id,
        label: this.$t(group.labelKey),
        items: group.items.map((item) => ({
          ...item,
          title: this.$t(item.titleKey),
        })),
      }))
    },
    page() {
      return this.pages[this.activeId] || this.pages.overview
    },
    exampleId() {
      return (this.templates[0] && this.templates[0].id) || '550e8400-e29b-41d4-a716-446655440000'
    },
    apiKey() {
      return (this.user && this.user.api_key) || ''
    },
    sampleKey() {
      if (!this.apiKey) return '<your-api-key>'
      return this.showKey ? this.apiKey : this.maskKey(this.apiKey)
    },
    keyDisplay() {
      if (!this.apiKey) return ''
      return this.showKey ? this.apiKey : this.maskKey(this.apiKey)
    },
    apiBase() {
      return String(process.env.VUE_APP_BASE_URL || '').replace(/\/$/, '')
    },
    mcpUrl() {
      return String(process.env.VUE_APP_MCP_URL || 'http://localhost:9010/mcp').replace(/\/$/, '')
    },
    n8nEditorUrl() {
      const raw = String(process.env.VUE_APP_N8N_URL || '').trim()
      if (!raw) return ''
      return raw.replace(/\/$/, '')
    },
    canvaStatusRows() {
      return [
        { status: 'queued', desc: this.$t('studioApi.canva_status_queued') },
        { status: 'fetching', desc: this.$t('studioApi.canva_status_fetching') },
        { status: 'downloading', desc: this.$t('studioApi.canva_status_downloading') },
        { status: 'saving', desc: this.$t('studioApi.canva_status_saving') },
        { status: 'done', desc: this.$t('studioApi.canva_status_done') },
        { status: 'error', desc: this.$t('studioApi.canva_status_error') },
      ]
    },
    canvaN8nFlows() {
      const base = this.apiBase || 'https://sociallabs.codelabs.id/api/v1'
      return [
        {
          id: 'canva-n8n',
          nodes: [
            { type: 'Trigger', name: 'Webhook / Manual', desc: this.$t('studioApi.n8n_node_manual') },
            {
              type: 'HTTP Request',
              name: 'Login',
              desc: this.$t('studioApi.canva_n8n_login'),
              config: [
                `POST ${base}/auth/login`,
                'JSON: { "email", "password" }',
                'Save data.token as Bearer',
              ].join('\n'),
            },
            {
              type: 'HTTP Request',
              name: 'Start import',
              config: [
                `POST ${base}/ai/studio/import-canva`,
                'Authorization: Bearer <token>',
                'JSON: { "url": "https://canva.link/…" }',
              ].join('\n'),
            },
            { type: 'Wait', name: 'Wait 3–5s', desc: this.$t('studioApi.canva_n8n_wait') },
            {
              type: 'HTTP Request',
              name: 'Get job',
              config: `GET ${base}/ai/studio/import-canva/jobs/{{ job.id }} + Bearer`,
            },
            { type: 'IF', name: 'Still running?', desc: this.$t('studioApi.canva_n8n_if') },
            {
              type: 'SocialLabs Studio',
              name: 'Render',
              desc: this.$t('studioApi.canva_n8n_render'),
              config: 'Design = job.template_id when status is done — credential SocialLabs Studio API (X-API-Key)',
            },
          ],
        },
      ]
    },
    isCanvaEndpoint() {
      return ['canvaStart', 'canvaImportList', 'canvaImportJob'].includes(this.page.id)
    },
    n8nFlows() {
      const base = this.apiBase || 'https://sociallabs.codelabs.id/api/v1'
      return [
        {
          id: 'render',
          title: this.$t('studioApi.n8n_workflow_render'),
          lead: this.$t('studioApi.n8n_workflow_render_desc'),
          nodes: [
            {
              type: 'Trigger',
              name: 'Manual Trigger',
              desc: this.$t('studioApi.n8n_node_manual'),
            },
            {
              type: 'SocialLabs Studio',
              name: 'Get Metadata (optional)',
              desc: this.$t('studioApi.n8n_node_get_meta'),
              config: [
                'Credential: SocialLabs Studio API',
                `Base URL: ${base}`,
                'Operation: Get Metadata',
                'Design: From list or UUID',
                'Page: 1 — enable Filter Get By Page to read field keys',
              ].join('\n'),
            },
            {
              type: 'SocialLabs Studio',
              name: 'Render',
              desc: this.$t('studioApi.n8n_node_render'),
              config: [
                'Operation: Render',
                'Design: same as above',
                'Format: png or jpeg',
                'Fields / Fields JSON: only non-empty overrides',
                'Output: binary image on item',
              ].join('\n'),
            },
          ],
        },
        {
          id: 'list',
          title: this.$t('studioApi.n8n_workflow_list'),
          lead: this.$t('studioApi.n8n_workflow_list_desc'),
          nodes: [
            {
              type: 'SocialLabs Studio',
              name: 'Get Designs',
              desc: this.$t('studioApi.n8n_node_list'),
            },
            {
              type: 'SocialLabs Studio',
              name: 'Render',
              desc: this.$t('studioApi.n8n_node_render_from_item'),
              config: 'Design ID: expression from previous item — Fields JSON e.g. { "page_1_text_1": "Hello" }',
            },
          ],
        },
      ]
    },
    mcpTools() {
      return [
        { name: 'studio_list_designs', desc: 'List saved designs (id, name, size, pages)' },
        { name: 'studio_get_design', desc: 'Read design size, pages, and editable field keys (page starts at 1)' },
        { name: 'studio_render_design', desc: 'Render PNG/JPEG matching Design Studio, with text/image overrides' },
      ]
    },
    mcpConfigSample() {
      return [
        '{',
        '  "mcpServers": {',
        '    "sociallabs-studio": {',
        `      "url": "${this.mcpUrl}",`,
        '      "headers": {',
        `        "X-API-Key": "${this.sampleKey}"`,
        '      }',
        '    }',
        '  }',
        '}',
      ].join('\n')
    },
    authMethods() {
      const key = this.sampleKey
      return [
        { name: 'X-API-Key', badge: 'recommended', example: `X-API-Key: ${key}` },
        { name: 'Authorization', example: `Authorization: ApiKey ${key}` },
      ]
    },
    errorRows() {
      return [
        { code: '401', label: 'Unauthorized', desc: 'Missing or invalid API key' },
        { code: '404', label: 'Not found', desc: 'Design does not exist or is not owned by this key' },
        { code: '400', label: 'Bad request', desc: 'Page number is missing, invalid, or out of range (pages start at 1)' },
        { code: '429', label: 'Too many requests', desc: 'Rate limit exceeded. Wait for Retry-After seconds, then retry.' },
      ]
    },
    rateLimitRows() {
      return [
        { name: 'List / metadata / account', limit: '60/min · 600/hour', desc: 'GET /public/studio, GET /public/studio/:id, GET /public/studio/account' },
        { name: 'Render', limit: '10/min · 120/hour', desc: 'GET and POST /public/studio/:id/download' },
        { name: 'Per IP', limit: '120/min', desc: 'Applies before API-key auth, including invalid keys' },
      ]
    },
    rateLimitHeaders() {
      return [
        { name: 'X-RateLimit-Limit', desc: 'Max requests in the current window' },
        { name: 'X-RateLimit-Remaining', desc: 'Requests left in the current window' },
        { name: 'X-RateLimit-Reset', desc: 'Unix timestamp when the window resets' },
        { name: 'Retry-After', desc: 'Seconds to wait (429 only)' },
      ]
    },
    flatNav() {
      const items = []
      this.navGroups.forEach((group) => {
        group.items.forEach((item) => items.push(item))
      })
      return items
    },
    navIndex() {
      return this.flatNav.findIndex((item) => item.id === this.activeId)
    },
    prevItem() {
      return this.navIndex > 0 ? this.flatNav[this.navIndex - 1] : null
    },
    nextItem() {
      return this.navIndex >= 0 && this.navIndex < this.flatNav.length - 1
        ? this.flatNav[this.navIndex + 1]
        : null
    },
    activeSchema() {
      const match = (this.page.responses || []).find((row) => row.status === this.activeResponse)
      const schema = (match && match.schema) || (this.page.responses[0] && this.page.responses[0].schema) || []
      return flattenSchema(schema, 0, '')
    },
    activeContentType() {
      const match = (this.page.responses || []).find((row) => row.status === this.activeResponse)
      if (this.tryImage) return 'image/png'
      return (match && match.contentType) || 'application/json'
    },
    requestSample() {
      if (this.page.id === 'mcp') return this.mcpConfigSample
      if (this.page.id === 'canva-import') return this.buildRequest('canvaStart', this.activeLang)
      return this.buildRequest(this.page.id, this.activeLang)
    },
    responseSample() {
      if (this.page.id === 'mcp') {
        return [
          '{',
          '  "tools": [',
          '    { "name": "studio_list_designs", "description": "List saved designs" },',
          '    { "name": "studio_get_design", "description": "Get design metadata and fields" },',
          '    { "name": "studio_render_design", "description": "Render design as PNG/JPEG matching Design Studio" }',
          '  ]',
          '}',
        ].join('\n')
      }
      if (this.tryOverride) return this.tryOverride
      if (this.activeResponse === '401') {
        return '{\n  "success": false,\n  "data": {},\n  "message": "Invalid API key"\n}'
      }
      if (this.activeResponse === '404') {
        return '{\n  "success": false,\n  "data": {},\n  "message": "Design not found"\n}'
      }
      if (this.activeResponse === '400') {
        return '{\n  "success": false,\n  "data": {},\n  "message": "Page not found"\n}'
      }
      if (this.activeResponse === '429') {
        return '{\n  "success": false,\n  "data": {},\n  "message": "Rate limit exceeded. Try again later."\n}'
      }
      if (this.page.id === 'downloadPost') {
        return '// binary image bytes\n// Content-Type: image/png'
      }
      if (this.page.id === 'canvaStart' || this.page.id === 'canva-import') {
        return [
          '{',
          '  "success": true,',
          '  "data": {',
          '    "job": {',
          '      "id": "550e8400-e29b-41d4-a716-446655440001",',
          '      "status": "queued",',
          '      "message": "Queued",',
          '      "percent": 0,',
          '      "url": "https://canva.link/example"',
          '    }',
          '  },',
          '  "message": "Queued"',
          '}',
        ].join('\n')
      }
      if (this.page.id === 'canvaImportList') {
        return [
          '{',
          '  "success": true,',
          '  "data": {',
          '    "jobs": [',
          '      { "id": "550e8400-e29b-41d4-a716-446655440001", "status": "done", "template_id": "' + this.exampleId + '" }',
          '    ]',
          '  },',
          '  "message": "OK"',
          '}',
        ].join('\n')
      }
      if (this.page.id === 'canvaImportJob') {
        return [
          '{',
          '  "success": true,',
          '  "data": {',
          '    "job": {',
          '      "id": "550e8400-e29b-41d4-a716-446655440001",',
          '      "status": "done",',
          '      "percent": 100,',
          '      "template_id": "' + this.exampleId + '",',
          '      "template_name": "Summer sale"',
          '    }',
          '  },',
          '  "message": "OK"',
          '}',
        ].join('\n')
      }
      if (this.page.id === 'list') {
        return [
          '{',
          '  "success": true,',
          '  "data": {',
          '    "designs": [',
          '      { "id": "' + this.exampleId + '", "name": "Summer sale", "width": 1080, "height": 1080, "pages": 1 }',
          '    ]',
          '  },',
          '  "message": "Success"',
          '}',
        ].join('\n')
      }
      return [
        '{',
        '  "success": true,',
        '  "data": {',
        '    "design": {',
        '      "id": "' + this.exampleId + '",',
        '      "name": "Summer sale",',
        '      "width": 1080,',
        '      "height": 1080,',
        '      "pages": 1,',
        '      "fields": [',
        '        { "key": "page_1_text_1", "type": "text", "label": "Page 1 Text 1", "value": "SALE", "page": 1 },',
        '        { "key": "page_1_image_1", "type": "image", "label": "Page 1 Image 1", "value": "https://…", "page": 1 }',
        '      ]',
        '    }',
        '  },',
        '  "message": "Success"',
        '}',
      ].join('\n')
    },
    highlightedRequest() {
      return this.highlightCode(this.requestSample, this.activeLang)
    },
    highlightedResponse() {
      return this.highlightCode(this.responseSample, 'json')
    },
  },
  watch: {
    activeId() {
      this.syncResponse()
      this.clearTry()
    },
  },
  async created() {
    const hash = (this.$route.hash || '').replace('#', '')
    if (hash === 'upload') this.activeId = 'n8n'
    else if (hash && this.pages[hash]) this.activeId = hash
    this.syncResponse()
    if (this.isLoggedIn) {
      try {
        await this.fetchProfile()
      } catch (e) {
        /* keep persisted user if profile fetch fails */
      }
      try {
        await this.fetchTemplates()
      } catch (e) {
        /* docs remain readable without private design list */
      }
    }
  },
  mounted() {
    this.setMetaDescription()
  },
  beforeDestroy() {
    this.clearTry()
    this.resetMetaDescription()
  },
  methods: {
    ...mapActions('ai', ['fetchTemplates']),
    ...mapActions('user', ['fetchProfile', 'resetApiKey']),
    maskKey(key) {
      if (!key) return ''
      if (key.length <= 8) return '••••••••'
      return `${key.slice(0, 8)}${'•'.repeat(Math.min(24, key.length - 8))}`
    },
    toggleGroup(id) {
      this.openGroups = { ...this.openGroups, [id]: !this.openGroups[id] }
    },
    syncResponse() {
      const allowed = (this.page.responses || []).map((row) => row.status)
      if (allowed.indexOf(this.activeResponse) === -1) {
        this.activeResponse = allowed[0] || '200'
      }
    },
    select(id) {
      if (!this.pages[id]) return
      this.activeId = id
      this.navOpen = false
      if (this.$route.hash !== `#${id}`) {
        this.$router.replace({ hash: id }).catch(() => {})
      }
      this.$nextTick(() => {
        if (this.$refs.main) this.$refs.main.scrollTop = 0
      })
    },
    docUrl(path) {
      return `${this.apiBase}${String(path || '').replace(':id', '{uuid}')}`
    },
    fullUrl(path) {
      return `${this.apiBase}${String(path || '').replace(':id', String(this.exampleId))}`
    },
    buildRequest(pageId, lang) {
      const key = this.sampleKey
      const bearer = '<access_token>'
      if (pageId === 'canvaStart') {
        const url = this.docUrl('/ai/studio/import-canva')
        const body = '{"url":"https://canva.link/example"}'
        if (lang === 'js') {
          return [
            `const login = await fetch("${this.docUrl('/auth/login')}", {`,
            `  method: "POST",`,
            `  headers: { "Content-Type": "application/json" },`,
            `  body: JSON.stringify({ email: "you@example.com", password: "…" }),`,
            `})`,
            `const { data: auth } = await login.json()`,
            `const res = await fetch("${url}", {`,
            `  method: "POST",`,
            `  headers: {`,
            `    "Content-Type": "application/json",`,
            `    Authorization: \`Bearer \${auth.token}\`,`,
            `  },`,
            `  body: '${body}',`,
            `})`,
            `const { data } = await res.json()`,
            `console.log(data.job.id)`,
          ].join('\n')
        }
        if (lang === 'python') {
          return [
            'import requests',
            '',
            `base = "${this.apiBase}"`,
            'auth = requests.post(f"{base}/auth/login", json={"email": "you@example.com", "password": "…"}).json()',
            'token = auth["data"]["token"]',
            `r = requests.post(f"{base}/ai/studio/import-canva",`,
            '    headers={"Authorization": f"Bearer {token}"},',
            '    json={"url": "https://canva.link/example"},',
            ')',
            'print(r.json()["data"]["job"]["id"])',
          ].join('\n')
        }
        return [
          `# 1) POST ${this.docUrl('/auth/login')} → token`,
          `curl --request POST \\`,
          `  --url "${url}" \\`,
          `  --header "Authorization: Bearer ${bearer}" \\`,
          `  --header "Content-Type: application/json" \\`,
          `  --data '${body}'`,
        ].join('\n')
      }
      if (pageId === 'canvaImportList') {
        const url = this.docUrl('/ai/studio/import-canva/jobs')
        if (lang === 'js') {
          return [
            `const res = await fetch("${url}", {`,
            `  headers: { Authorization: "Bearer ${bearer}" },`,
            `})`,
            `const { data } = await res.json()`,
            `console.log(data.jobs)`,
          ].join('\n')
        }
        if (lang === 'python') {
          return [
            'import requests',
            '',
            `r = requests.get("${url}", headers={"Authorization": "Bearer ${bearer}"})`,
            'print(r.json()["data"]["jobs"])',
          ].join('\n')
        }
        return [
          `curl --request GET \\`,
          `  --url "${url}" \\`,
          `  --header "Authorization: Bearer ${bearer}"`,
        ].join('\n')
      }
      if (pageId === 'canvaImportJob') {
        const url = this.docUrl('/ai/studio/import-canva/jobs/:id')
        if (lang === 'js') {
          return [
            `const jobId = "550e8400-e29b-41d4-a716-446655440001"`,
            `const res = await fetch("${url}".replace(":id", jobId), {`,
            `  headers: { Authorization: "Bearer ${bearer}" },`,
            `})`,
            `const { data } = await res.json()`,
            `console.log(data.job.status, data.job.template_id)`,
          ].join('\n')
        }
        if (lang === 'python') {
          return [
            'import requests',
            '',
            `job_id = "550e8400-e29b-41d4-a716-446655440001"`,
            `r = requests.get("${url}".replace(":id", job_id), headers={"Authorization": "Bearer ${bearer}"})`,
            'job = r.json()["data"]["job"]',
            'print(job["status"], job.get("template_id"))',
          ].join('\n')
        }
        return [
          `curl --request GET \\`,
          `  --url "${url}" \\`,
          `  --header "Authorization: Bearer ${bearer}"`,
        ].join('\n')
      }
      if (pageId === 'list') {
        const url = this.docUrl('/public/studio')
        if (lang === 'js') {
          return [
            `const res = await fetch("${url}", {`,
            `  headers: { "X-API-Key": "${key}" }`,
            `})`,
            `const { data } = await res.json()`,
            `console.log(data.designs)`,
          ].join('\n')
        }
        if (lang === 'python') {
          return [
            'import requests',
            '',
            `r = requests.get("${url}", headers={"X-API-Key": "${key}"})`,
            'print(r.json()["data"]["designs"])',
          ].join('\n')
        }
        return [
          `curl --request GET \\`,
          `  --url "${url}" \\`,
          `  --header "X-API-Key: ${key}"`,
        ].join('\n')
      }
      if (pageId === 'downloadPost') {
        const url = this.docUrl('/public/studio/:id/download')
        const json = '{"page":1,"format":"png","fields":{"page_1_text_1":"50% OFF","page_1_image_1":"https://example.com/hero.jpg"}}'
        if (lang === 'js') {
          return [
            `const res = await fetch("${url}", {`,
            `  method: "POST",`,
            `  headers: {`,
            `    "Content-Type": "application/json",`,
            `    "X-API-Key": "${key}"`,
            `  },`,
            `  body: JSON.stringify({`,
            `    page: 1,`,
            `    format: "png",`,
            `    fields: { page_1_text_1: "50% OFF", page_1_image_1: "https://example.com/hero.jpg" }`,
            `  })`,
            `})`,
            `const blob = await res.blob()`,
          ].join('\n')
        }
        if (lang === 'python') {
          return [
            'import requests',
            '',
            `r = requests.post("${url}", headers={"X-API-Key": "${key}"}, json={`,
            '    "page": 1,',
            '    "format": "png",',
            '    "fields": {"page_1_text_1": "50% OFF", "page_1_image_1": "https://example.com/hero.jpg"},',
            '})',
            'open("design.png", "wb").write(r.content)',
          ].join('\n')
        }
        return [
          `curl --request POST \\`,
          `  --url "${url}" \\`,
          `  --header "X-API-Key: ${key}" \\`,
          `  --header "Content-Type: application/json" \\`,
          `  --data '${json}' \\`,
          `  --output design.png`,
        ].join('\n')
      }

      const url = `${this.docUrl('/public/studio/:id')}?page=1`
      if (lang === 'js') {
        return [
          `const res = await fetch("${url}", {`,
          `  headers: { "X-API-Key": "${key}" }`,
          `})`,
          `const { data } = await res.json()`,
          `console.log(data.design.fields)`,
        ].join('\n')
      }
      if (lang === 'python') {
        return [
          'import requests',
          '',
          `r = requests.get("${url}", headers={"X-API-Key": "${key}"})`,
          'design = r.json()["data"]["design"]',
          'print(design["fields"])',
        ].join('\n')
      }
      return [
        `curl --request GET \\`,
        `  --url "${url}" \\`,
        `  --header "X-API-Key: ${key}"`,
      ].join('\n')
    },
    highlightCode(src, lang) {
      const escaped = escapeHtml(src)
      if (lang === 'json' || src.trim().startsWith('{')) {
        return escaped.replace(/(&quot;[^&]*&quot;)(\s*:)?|\b(-?\d+(?:\.\d+)?)\b|\b(true|false|null)\b|(\/\/[^\n]*)/g, (m, str, colon, num, kw, comment) => {
          if (comment) return `<span class="tok-comment">${comment}</span>`
          if (colon) return `<span class="tok-key">${str}</span>${colon}`
          if (str) return `<span class="tok-str">${str}</span>`
          if (num) return `<span class="tok-num">${num}</span>`
          if (kw) return `<span class="tok-bool">${kw}</span>`
          return m
        })
      }
      return escaped
        .replace(/(&quot;[^&]*&quot;|&#39;[^&]*&#39;)/g, '<span class="tok-str">$1</span>')
        .replace(/\b(curl|const|await|fetch|import|print|requests)\b/g, '<span class="tok-kw">$1</span>')
        .replace(/--[a-zA-Z-]+/g, '<span class="tok-flag">$&</span>')
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
      })
    },
    clearTry() {
      if (this.tryImage) URL.revokeObjectURL(this.tryImage)
      this.tryImage = ''
      this.tryOverride = ''
    },
    async tryRequest() {
      if (['canvaStart', 'canvaImportList', 'canvaImportJob'].includes(this.page.id)) {
        this.$toast.error(this.$t('studioApi.canva_bearer_required'))
        return
      }
      if (!this.apiKey) {
        this.$toast.error(this.$t('studioApi.generate_key_first'))
        this.select('api-key')
        return
      }
      if (this.page.id !== 'list' && !this.templates.length) {
        this.$toast.error(this.$t('studioApi.create_design_first'))
        this.select('designs')
        return
      }
      this.trying = true
      this.clearTry()
      try {
        const headers = { 'X-API-Key': this.apiKey }
        let url
        let options = { method: 'GET', headers }
        if (this.page.id === 'downloadPost') {
          url = this.fullUrl('/public/studio/:id/download')
          options = {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ page: 1, format: 'png', fields: {} }),
          }
        } else if (this.page.id === 'list') {
          url = `${this.apiBase}/public/studio`
        } else {
          url = `${this.fullUrl('/public/studio/:id')}?page=1`
        }
        const res = await fetch(url, options)
        const type = res.headers.get('content-type') || ''
        if (type.indexOf('application/json') !== -1) {
          const json = await res.json()
          this.tryOverride = JSON.stringify(json, null, 2)
          this.activeResponse = String(res.status)
        } else {
          const blob = await res.blob()
          this.tryImage = URL.createObjectURL(blob)
          this.activeResponse = String(res.status)
        }
      } catch (e) {
        this.$toast.error(this.$t('studioApi.request_failed'))
      } finally {
        this.trying = false
      }
    },
    downloadPostmanCollection() {
      const designId = (this.templates && this.templates[0] && this.templates[0].id) || ''
      try {
        downloadStudioPostmanCollection(
          buildStudioPostmanCollection({
            apiKey: this.apiKey || '',
            designId,
            page: 1,
            format: 'png',
          }),
        )
        this.$toast.success(this.$t('studioApi.open_in_postman_done'))
      } catch (e) {
        this.$toast.error(this.$t('studioApi.open_in_postman_failed'))
      }
    },
    async onResetKey() {
      const hadKey = !!this.apiKey
      if (hadKey) {
        const ok = await this.$confirm(this.$t('studioApi.regenerate_confirm_full'), { danger: true })
        if (!ok) return
      }
      this.resettingKey = true
      try {
        await this.resetApiKey()
        this.$toast.success(hadKey ? this.$t('studioApi.key_regenerated') : this.$t('studioApi.key_generated'))
      } catch (e) {
        this.$toast.error((e.response && e.response.data && e.response.data.message) || this.$t('studioApi.failed_generate_key'))
      } finally {
        this.resettingKey = false
      }
    },
    async copyText(text) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          const el = document.createElement('textarea')
          el.value = text
          document.body.appendChild(el)
          el.select()
          document.execCommand('copy')
          document.body.removeChild(el)
        }
        this.$toast.success(this.$t('studioApi.copied'))
      } catch (e) {
        this.$toast.error(this.$t('studioApi.copy_failed'))
      }
    },
    setMetaDescription() {
      const content = this.$t('studioApi.public_meta_description')
      let el = document.querySelector('meta[name="description"]')
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute('name', 'description')
        document.head.appendChild(el)
      }
      this._prevMetaDescription = el.getAttribute('content') || ''
      el.setAttribute('content', content)
    },
    resetMetaDescription() {
      const el = document.querySelector('meta[name="description"]')
      if (el && this._prevMetaDescription !== undefined) {
        if (this._prevMetaDescription) el.setAttribute('content', this._prevMetaDescription)
        else el.removeAttribute('content')
      }
    },
  },
}
</script>

<style scoped>
.studio-docs {
  min-height: 100vh;
  background: var(--wc-bg-page);
}
.studio-docs--embedded {
  min-height: 0;
}

.docs-public-nav {
  position: sticky;
  top: 0;
  z-index: 30;
  background: var(--wc-bg-card);
  border-bottom: 1px solid var(--wc-border);
}

.docs-public-nav__inner {
  max-width: 1320px;
  margin: 0 auto;
  padding: 0.65rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.docs-public-nav__logo {
  height: 28px;
  width: auto;
}

.docs-public-nav__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.docs-card--cta {
  text-align: left;
}

.docs-page {
  --bg: var(--wc-bg-card);
  --bg2: var(--wc-bg-subtle);
  --panel: var(--wc-bg-card);
  --border: var(--wc-border);
  --text: var(--wc-text-heading);
  --muted: var(--wc-text);
  --faint: var(--wc-text-muted);
  --get: var(--wc-success);
  --post: var(--wc-info);
  --code: #1e2433;
  --hover: var(--wc-bg-subtle);
  --active: var(--wc-primary-light);
  --radius: var(--wc-radius-lg);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(300px, 360px);
  gap: 1.25rem;
  align-items: start;
}
.docs {
  overflow: hidden;
  padding: 0 !important;
  color: var(--text);
}
.docs button {
  font-family: inherit;
  cursor: pointer;
}
.docs-icon-btn {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  color: var(--faint);
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.docs-icon-btn:hover {
  background: var(--hover);
  color: var(--text);
}
.docs-icon-btn:disabled {
  opacity: 0.5;
}
.docs-shell {
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  position: relative;
  min-height: 560px;
}
.docs-nav {
  border-right: 1px solid var(--border);
  padding: 16px 10px 24px;
  background: var(--bg);
  align-self: stretch;
}
.docs-group {
  margin-bottom: 8px;
}
.docs-group__head {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: none;
  border: 0;
  color: var(--text);
  font-size: 13px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 8px;
}
.docs-group__head i {
  font-size: 10px;
  color: var(--faint);
}
.docs-nav-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: none;
  border: 0;
  color: var(--muted);
  font-size: 13px;
  padding: 7px 10px;
  border-radius: 8px;
  text-align: left;
}
.docs-nav-item:hover {
  background: var(--hover);
  color: var(--text);
}
.docs-nav-item.active {
  background: var(--active);
  color: var(--wc-primary);
}
.docs-nav-item__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.docs-verb {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  flex-shrink: 0;
}
.docs-verb.get { color: var(--get); }
.docs-verb.post { color: var(--post); }
.docs-main {
  padding: 24px 28px 32px;
  min-width: 0;
}
.docs-kicker {
  color: var(--faint);
  font-size: 13px;
  margin-bottom: 6px;
}
.docs-main h1,
.docs-main h2,
.docs-main h3 {
  color: var(--text);
}
.docs-main h1 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 12px;
  letter-spacing: -0.02em;
}
.docs-main h2 {
  font-size: 22px;
  font-weight: 600;
  margin: 28px 0 10px;
}
.docs-main h3 {
  font-size: 14px;
  font-weight: 600;
  margin: 24px 0 10px;
}
.docs-base {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 8px 10px 8px 12px;
  margin-bottom: 14px;
}
.docs-base code {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: var(--muted);
  background: none;
  word-break: break-all;
}
.docs-lead,
.docs-copy {
  color: var(--muted);
  font-size: 14px;
  line-height: 1.65;
}
.docs-copy code,
.docs-steps code,
.docs-kv code {
  background: var(--hover);
  color: var(--text);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 12px;
}
.docs-steps {
  color: var(--muted);
  padding-left: 18px;
  margin: 0 0 18px;
}
.docs-steps li {
  margin-bottom: 8px;
}
.docs-n8n-flow {
  margin-bottom: 2rem;
}
.docs-n8n-nodes {
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.docs-n8n-nodes > li {
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 12px 14px;
  background: var(--wc-bg-subtle, #f7f8fb);
}
.docs-n8n-node__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.docs-n8n-node__type {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: 6px;
  background: var(--wc-primary-light, #ede9fe);
  color: var(--wc-primary, #5b4dff);
}
.docs-n8n-node__desc {
  margin: 0 0 8px;
  font-size: 13px;
}
.docs-code--compact {
  margin: 0;
  font-size: 11px;
  max-height: 220px;
}
.docs-kv {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  font-size: 13px;
  margin-bottom: 10px;
}
.docs-kv span {
  color: var(--faint);
  font-weight: 600;
  min-width: 48px;
  flex-shrink: 0;
}
.docs-kv code {
  flex: 1;
  min-width: 0;
  word-break: break-all;
}
.docs-endpoint {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  width: 100%;
  margin-bottom: 12px;
}
.docs-endpoint code {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  background: none;
  color: var(--text);
  word-break: break-all;
}
.docs-schema {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--panel);
}
.docs-schema--flush {
  border: 0;
  border-radius: 0;
}
.docs-schema__row {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
}
.docs-schema__row:last-child {
  border-bottom: 0;
}
.docs-schema__name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.docs-schema__name code {
  background: none;
  color: var(--text);
  font-size: 13px;
}
.docs-type,
.docs-in {
  color: var(--faint);
  font-size: 12px;
}
.docs-req,
.docs-pill {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--wc-primary);
  border: 1px solid rgba(var(--wc-primary-rgb), 0.35);
  padding: 1px 6px;
  border-radius: 999px;
}
.docs-schema__desc {
  margin: 4px 0 0;
  color: var(--muted);
  font-size: 13px;
}
.docs-schema__desc code {
  background: var(--hover);
  color: var(--text);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 12px;
}
.docs-resp {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--panel);
}
.docs-resp__tabs {
  display: flex;
  gap: 2px;
  padding: 8px 8px 0;
  border-bottom: 1px solid var(--border);
}
.docs-resp__tab {
  background: none;
  border: 0;
  color: var(--muted);
  font-size: 12px;
  font-weight: 600;
  padding: 8px 12px;
  border-bottom: 2px solid transparent;
}
.docs-resp__tab.active {
  color: var(--wc-primary);
  border-bottom-color: var(--wc-primary);
}
.docs-card {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  padding: 16px;
  background: var(--panel);
}
.docs-card h3 {
  margin: 0 0 4px;
  font-size: 15px;
}
.docs-card p {
  margin: 0;
  color: var(--muted);
  font-size: 13px;
}
.docs-card__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
}
.docs-postman {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.docs-key {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  margin-top: 14px;
  flex-wrap: wrap;
}
.docs-key code {
  flex: 1;
  min-width: 180px;
  background: var(--hover);
  color: var(--text);
  padding: 8px 10px;
  border-radius: 8px;
  word-break: break-all;
}
.docs-btn {
  border: 1px solid var(--border);
  background: var(--hover);
  color: var(--text);
  font-size: 12px;
  font-weight: 600;
  padding: 7px 12px;
  border-radius: 8px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}
.docs-btn:hover {
  color: var(--text);
  text-decoration: none;
}
.docs-btn--ghost {
  background: transparent;
}
.docs-empty {
  color: var(--muted);
  font-size: 13px;
  padding: 16px 0;
}
.docs-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius);
}
.docs-table {
  width: 100%;
  margin: 0;
  font-size: 13px;
}
.docs-table th,
.docs-table td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  vertical-align: middle;
}
.docs-table th {
  color: var(--faint);
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.docs-table td code {
  background: var(--hover);
  color: var(--text);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}
.docs-table .muted {
  color: var(--muted);
}
.docs-pager {
  display: flex;
  justify-content: space-between;
  margin-top: 48px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}
.docs-pager__btn {
  background: none;
  border: 0;
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
}
.docs-pager__btn:hover {
  color: var(--text);
}
.docs-pager__btn--next {
  margin-left: auto;
}
.docs-side {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  background: var(--bg2);
  position: sticky;
  top: 24px;
  align-self: start;
}
.docs-panel {
  background: var(--code);
  border: 1px solid #2a3144;
  border-radius: var(--wc-radius-lg);
  overflow: hidden;
  color: #e8ecf3;
}
.docs-panel .docs-icon-btn {
  color: #9aa3b5;
}
.docs-panel .docs-icon-btn:hover {
  background: #2a3144;
  color: #fff;
}
.docs-panel__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  border-bottom: 1px solid #2a3144;
}
.docs-panel__tools {
  display: flex;
  align-items: center;
  gap: 4px;
}
.docs-select {
  background: #2a3144;
  color: #e8ecf3;
  border: 1px solid #3a4258;
  border-radius: 6px;
  font-size: 12px;
  padding: 4px 8px;
}
.docs-info {
  color: #71717a;
  margin-left: 6px;
  font-size: 11px;
}
.docs-code {
  margin: 0;
  padding: 14px 16px;
  font-size: 12px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  color: #e8ecf3;
  background: var(--code);
}
.docs-code ::v-deep .tok-key { color: #7dd3fc; }
.docs-code ::v-deep .tok-str { color: #86efac; }
.docs-code ::v-deep .tok-num { color: #fbbf24; }
.docs-code ::v-deep .tok-bool { color: #c4b5fd; }
.docs-code ::v-deep .tok-kw { color: #93c5fd; }
.docs-code ::v-deep .tok-flag { color: #fda4af; }
.docs-code ::v-deep .tok-comment { color: #71717a; }
.docs-panel__foot {
  padding: 8px 12px;
  font-size: 11px;
  color: #71717a;
  border-top: 1px solid #2a3144;
}
.docs-preview {
  padding: 12px;
  background: #161b27;
}
.docs-preview img {
  max-width: 100%;
  border-radius: 8px;
  display: block;
}
.docs-scrim {
  display: none;
}
@media (max-width: 1199px) {
  .docs-page {
    grid-template-columns: 1fr;
  }
  .docs-shell {
    grid-template-columns: 200px minmax(0, 1fr);
  }
  .docs-side {
    position: static;
  }
  .docs-nav {
    position: static;
  }
}
@media (max-width: 991px) {
  .docs-page {
    gap: 1rem;
  }
  .docs-shell {
    grid-template-columns: 1fr;
  }
  .docs-nav {
    display: none;
    position: absolute;
    z-index: 20;
    top: 0;
    left: 0;
    bottom: auto;
    width: 260px;
    max-height: 80vh;
    background: #fff;
    box-shadow: var(--wc-shadow-lg);
    border-right: 1px solid var(--border);
    border-radius: 0 0 var(--wc-radius-md) 0;
  }
  .docs-nav.open {
    display: block;
  }
  .docs-scrim {
    display: block;
    position: absolute;
    inset: 0;
    background: rgba(39, 43, 65, 0.35);
    z-index: 15;
  }
  .docs-main {
    padding: 20px 16px 28px;
  }
}
</style>
